import { useState, useCallback, useEffect, useRef } from "react";

import "../../assets/css/news.scss";
import Header from "../../components/header";
import NewsFooter from "../../components/news_footer";
import {
  getCategoryList,
  searchDailyQuiz,
  searchDetailDailyQuiz,
} from "../../api/brainlab";
import {
  parseContent,
  stripHtmlTags,
  convertNewlinesToBr,
} from "../../utils/quill_parser_util";

import imageBanner from "../../assets/images/news/banner.png";

// 전체 탭 식별자
const ALL_TAB = "all";

/**
 * 서버 color(ARGB, 예: "FF00B6B6") → CSS hex(#RRGGBB)
 */
const argbToHex = (argb) => {
  if (!argb || typeof argb !== "string") return "#888888";
  const clean = argb.replace(/^#/, "");
  // 8자리(AARRGGBB)면 알파 제거, 6자리면 그대로
  const rgb = clean.length === 8 ? clean.slice(2) : clean;
  return `#${rgb}`;
};

function BrainLabPage() {
  const [categories, setCategories] = useState([
    { key: ALL_TAB, title: "전체", color: null },
  ]);
  const [activeTab, setActiveTab] = useState(ALL_TAB);

  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const [quizData, setQuizData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 12;
  const pageGroupSize = 5;

  const modalRef = useRef(null);

  // ─── 카테고리 목록 조회 (맨 앞에 "전체" 고정) ───
  useEffect(() => {
    const fetchCategories = async () => {
      const result = await getCategoryList();
      if (result.success && result.data) {
        const sorted = [...result.data].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        const mapped = sorted.map((c) => ({
          key: String(c.categorySeq),
          title: c.title,
          color: c.color,
        }));
        setCategories([{ key: ALL_TAB, title: "전체", color: null }, ...mapped]);
      }
    };
    fetchCategories();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const datePart = dateString.split(" ")[0].split("T")[0];
    return datePart.replace(/-/g, ".");
  };

  // ─── 퀴즈 데이터 → 카드 데이터 변환 ───
  const transformQuizData = (quizList) => {
    return quizList.map((item) => {
      const parsedContent = parseContent(item.question || "");

      let extractedImage = null;
      let contentWithoutImage = parsedContent;

      const imgRegex = /<img[^>]+src=["']([^"'>]+)["']/i;
      const imgMatch = parsedContent.match(imgRegex);
      if (imgMatch && imgMatch[1]) {
        extractedImage = imgMatch[1];
        contentWithoutImage = parsedContent.replace(/<img[^>]*>/gi, "").trim();
      }

      const plainDesc = stripHtmlTags(contentWithoutImage);

      return {
        id: item.quizSeq,
        quizSeq: item.quizSeq,
        badge: item.categoryTitle || "브레인랩",
        badgeColor: argbToHex(item.categoryColor),
        title: item.title || "",
        desc: plainDesc,
        date: formatDate(item.questionDate),
        image: extractedImage,
        originalData: item,
      };
    });
  };

  // ─── 목록 조회 ───
  const fetchQuizList = useCallback(
    async (page) => {
      setIsLoading(true);
      try {
        const categorySeq = activeTab === ALL_TAB ? null : activeTab;
        const result = await searchDailyQuiz(categorySeq, page - 1, pageSize);

        if (result.success && result.data) {
          setQuizData(transformQuizData(result.data));
          setTotalPages(result.totalPages || 1);
          setTotalElements(result.totalElements || 0);
        } else {
          console.error("Failed to fetch quiz list:", result.error);
          setQuizData([]);
          setTotalPages(1);
          setTotalElements(0);
        }
      } catch (error) {
        console.error("Error fetching quiz list:", error);
        setQuizData([]);
        setTotalPages(1);
        setTotalElements(0);
      } finally {
        setIsLoading(false);
      }
    },
    [activeTab, pageSize]
  );

  useEffect(() => {
    fetchQuizList(currentPage);
  }, [currentPage, fetchQuizList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // ─── 상세 모달 ───
  const openModal = async (card) => {
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
    setIsDetailLoading(true);

    // 우선 카드 기본 정보로 표시
    setSelectedQuiz({
      badge: card.badge,
      badgeColor: card.badgeColor,
      date: card.date,
      title: card.title,
      image: card.image,
      contentHtml: "",
      answerHtml: "",
    });

    try {
      const result = await searchDetailDailyQuiz(card.quizSeq);
      if (result.success && result.data) {
        const d = result.data;
        const contentHtml = parseContent(d.question || "");
        const answerHtml = d.answer ? convertNewlinesToBr(d.answer) : "";

        setSelectedQuiz({
          badge: d.categoryTitle || card.badge,
          badgeColor: argbToHex(d.categoryColor) || card.badgeColor,
          date: formatDate(d.questionDate),
          title: d.title || card.title,
          image: card.image,
          contentHtml,
          answerHtml,
        });
      }
    } catch (error) {
      console.error("Error fetching quiz detail:", error);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "";
    setTimeout(() => setSelectedQuiz(null), 200);
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isModalOpen) closeModal();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isModalOpen]);

  // ─── 페이지네이션 ───
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageGroup = () => {
    const startPage =
      Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
    return { startPage, endPage };
  };

  const { startPage, endPage } = getPageGroup();

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePrevGroup = () => {
    if (startPage > 1) setCurrentPage(startPage - 1);
  };

  const handleNextGroup = () => {
    if (endPage < totalPages) setCurrentPage(endPage + 1);
  };

  return (
    <div className="news-body">
      <Header variant="subpage" />

      {/* 브레인랩 페이지 본문 */}
      <section className="news-page-section">
        <div className="news-container">
          <div className="news-page-header">
            <h1 className="news-page-title">브레인랩</h1>
            <p className="news-page-subtitle">
              매일 새로운 문제로 생각하는 힘을 키워보세요
            </p>
          </div>

          {/* 카테고리 탭 */}
          <div className="news-tabs-v2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                className={`news-tab-v2 ${activeTab === cat.key ? "active" : ""}`}
                onClick={() => setActiveTab(cat.key)}
              >
                {cat.title}
              </button>
            ))}
          </div>

          <div class="news-banner">
            <img
              src={imageBanner}
              alt="SK텔레콤 2026 ZEM폰 포켓피스 구매 고객 이벤트"
            />
          </div>

          {/* 카드 그리드 */}
          {isLoading ? (
            <div className="news-loading-state">
              <p>문제를 불러오는 중...</p>
            </div>
          ) : quizData.length > 0 ? (
            <div className="news-page-grid">
              {quizData.map((item) => (
                <article
                  className={`news-card ${item.image ? "news-card--with-image" : ""}`}
                  key={item.id}
                >
                  <span
                    className="news-category"
                    style={{
                      backgroundColor: item.badgeColor,
                      color: "#fff",
                    }}
                  >
                    {item.badge}
                  </span>
                  <div className="news-card-content">
                    <time className="news-card-date">{item.date}</time>
                    <h3 className="news-card-title">{item.title}</h3>
                    {item.image && (
                      <div className="news-card-image">
                        <img src={item.image} alt={item.title} loading="lazy" />
                      </div>
                    )}
                    <p className="news-card-excerpt">{item.desc}</p>
                    <a
                      href="#"
                      className="news-card-link"
                      onClick={(e) => {
                        e.preventDefault();
                        openModal(item);
                      }}
                    >
                      자세히 보기 →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="news-empty-state">
              <p>등록된 문제가 없습니다.</p>
            </div>
          )}

          {/* 페이지네이션 */}
          {!isLoading && quizData.length > 0 && totalPages > 1 && (
            <div className="news-pagination">
              <button
                className="pagination-btn"
                onClick={handlePrevGroup}
                disabled={startPage === 1}
              >
                이전
              </button>
              <div className="pagination-numbers">
                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`pagination-number ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              <button
                className="pagination-btn"
                onClick={handleNextGroup}
                disabled={endPage >= totalPages}
              >
                다음
              </button>
            </div>
          )}
        </div>
      </section>

      <NewsFooter variant="white" />

      {/* 상세 모달 */}
      <div
        className={`news-modal ${isModalOpen ? "active" : ""}`}
        ref={modalRef}
      >
        <div className="news-modal-overlay" onClick={closeModal}></div>
        <div className="news-modal-content">
          <button
            className="news-modal-close"
            aria-label="모달 닫기"
            onClick={closeModal}
          ></button>
          {selectedQuiz && (
            <div className="news-modal-body">
              <span
                className="news-modal-category news-category"
                style={{
                  backgroundColor: selectedQuiz.badgeColor,
                  color: "#fff",
                }}
              >
                {selectedQuiz.badge}
              </span>
              <time className="news-modal-date">{selectedQuiz.date}</time>
              <h2 className="news-modal-title">{selectedQuiz.title}</h2>

              {isDetailLoading ? (
                <div className="news-loading-state">
                  <p>내용을 불러오는 중...</p>
                </div>
              ) : (
                <>
                  {selectedQuiz.contentHtml && (
                    <div
                      className="news-modal-text"
                      dangerouslySetInnerHTML={{
                        __html: selectedQuiz.contentHtml,
                      }}
                    ></div>
                  )}
                  {selectedQuiz.answerHtml && (
                    <div className="news-modal-text" style={{ marginTop: "24px" }}>
                      <h3 style={{ marginBottom: "12px" }}>정답 및 해설</h3>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: selectedQuiz.answerHtml,
                        }}
                      ></div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BrainLabPage;
